import api from './api.service';
import type { CustomerOutstandingDetailRow } from './financeDashboard.service';

export type { CustomerOutstandingDetailRow };

export interface PendingReviewSalesOrderRow {
  id: string;
  sales_order_number: string;
  customer_name: string;
  grand_total: number;
  order_date: string;
  required_date: string;
}

export interface OrderInProgressRow {
  id: string;
  sales_order_number: string;
  customer_name: string;
  grand_total: number;
  required_date: string;
  promised_date: string;
  order_status: string;
  fulfillment_flag?: 'partial' | 'on_track';
  required_date_overdue?: boolean;
}

export interface SalesOrderPendingWorkOrderRow {
  id: string;
  sales_order_number: string;
  customer_name: string;
  order_date: string;
  required_date: string;
  grand_total: number;
  days_since_approved: number;
}

/** @deprecated Use DeliveryQueueRow */
export type DeliveryNotInvoicedRow = DeliveryQueueRow;

export interface DeliveryQueueRow {
  id: string;
  delivery_note_number: string;
  delivery_date: string;
  sales_order_id: string;
  customer_id: string;
  customer_name?: string;
  delivery_status?: string;
  document_package_status?: string | null;
}

export interface DeliveryQueues {
  delivered_not_invoiced: DeliveryQueueRow[];
  partially_invoiced: DeliveryQueueRow[];
  in_transit: DeliveryQueueRow[];
  doc_package_pending: DeliveryQueueRow[];
}

export interface OverdueCustomerArRow {
  customer_id: string;
  customer_name: string;
  balance_due: number;
  invoice_count: number;
  max_overdue_days: number;
}

export interface SalesDashboardActionSummary {
  overdue_customer_invoices: CustomerOutstandingDetailRow[];
  due_today_customer_invoices: CustomerOutstandingDetailRow[];
  due_soon_customer_invoices: CustomerOutstandingDetailRow[];
  ar_overdue_total: number;
  ar_due_today_total: number;
  ar_due_soon_total: number;
  overdue_ar_by_customer: OverdueCustomerArRow[];
  due_soon_days: number;
  pending_review_sales_orders: PendingReviewSalesOrderRow[];
  orders_in_progress: OrderInProgressRow[];
  sales_orders_pending_work_order: SalesOrderPendingWorkOrderRow[];
  deliveries_not_invoiced: DeliveryQueueRow[];
  delivery_queues: DeliveryQueues;
  draft_sales_order_count: number;
  draft_sales_invoice_count: number;
}

export interface SalesTeamPipelineRow {
  team_id: string;
  team_name: string;
  open_opportunities: number;
  pipeline_value: number;
  open_quotations: number;
  quotation_value: number;
  draft_sales_orders: number;
  booked_sales_orders: number;
  booked_sales_order_value: number;
  draft_boqs: number;
  draft_tre_estimations: number;
}

export interface CrmFollowUpRow {
  id: string;
  follow_up_type: string;
  title: string;
  subtitle?: string;
  due_or_reference_date?: string;
  entity_type: string;
  link_path: string;
}

export interface CrmFollowUpsSummary {
  stale_opportunities: CrmFollowUpRow[];
  overdue_activities: CrmFollowUpRow[];
  quotations_expiring: CrmFollowUpRow[];
  missing_customer_po: CrmFollowUpRow[];
}

export interface PendingApprovalRow {
  approval_instance_id: string;
  entity_type: string;
  entity_id: string;
  document_number: string | null;
  submitted_at: string | null;
  step_number: number;
  link_path: string;
}

export interface OrderAtRiskRow {
  sales_order_id: string;
  sales_order_number: string;
  customer_name: string;
  required_date: string;
  promised_date: string;
  work_order_id: string | null;
  work_order_number: string | null;
  readiness_status: string;
  blocking_reason: string | null;
}

export interface KpiTrendPoint {
  date: string;
  value: number;
}

export interface SalesDashboardKpiTrends {
  ar_overdue_balance_14d: KpiTrendPoint[];
  invoices_posted_count_14d: KpiTrendPoint[];
  so_confirmed_count_7d: KpiTrendPoint[];
  avg_delivery_to_invoice_lag_days: number | null;
}

export interface OrderHealthSignal {
  sales_order_id: string;
  level: 'green' | 'amber' | 'red';
  summary: string;
}

function unwrap<T>(res: unknown): T {
  const r = res as { data?: { data?: T } | T };
  return (r?.data as { data?: T })?.data ?? (r?.data as T) ?? (res as T);
}

export function emptySalesDashboardSummary(): SalesDashboardActionSummary {
  return {
    overdue_customer_invoices: [],
    due_today_customer_invoices: [],
    due_soon_customer_invoices: [],
    ar_overdue_total: 0,
    ar_due_today_total: 0,
    ar_due_soon_total: 0,
    overdue_ar_by_customer: [],
    due_soon_days: 7,
    pending_review_sales_orders: [],
    orders_in_progress: [],
    sales_orders_pending_work_order: [],
    deliveries_not_invoiced: [],
    delivery_queues: {
      delivered_not_invoiced: [],
      partially_invoiced: [],
      in_transit: [],
      doc_package_pending: [],
    },
    draft_sales_order_count: 0,
    draft_sales_invoice_count: 0,
  };
}

export async function getSalesTeamPipelineSummary(): Promise<SalesTeamPipelineRow[]> {
  const res = await api.get('/sales/dashboard/team-pipeline');
  const data = unwrap<SalesTeamPipelineRow[]>(res);
  return Array.isArray(data) ? data : [];
}

export async function getSalesDashboardActionSummary(params?: {
  as_on_date?: string;
  due_soon_days?: number;
}): Promise<SalesDashboardActionSummary> {
  const res = await api.get('/sales/dashboard/action-summary', {
    params: {
      ...(params?.as_on_date ? { as_on_date: params.as_on_date } : {}),
      ...(params?.due_soon_days != null ? { due_soon_days: params.due_soon_days } : {}),
    },
  });
  const data = unwrap<SalesDashboardActionSummary>(res);
  if (!data || typeof data !== 'object') {
    return emptySalesDashboardSummary();
  }
  const dq = data.delivery_queues;
  return {
    overdue_customer_invoices: Array.isArray(data.overdue_customer_invoices) ? data.overdue_customer_invoices : [],
    due_today_customer_invoices: Array.isArray(data.due_today_customer_invoices) ? data.due_today_customer_invoices : [],
    due_soon_customer_invoices: Array.isArray(data.due_soon_customer_invoices) ? data.due_soon_customer_invoices : [],
    ar_overdue_total: Number(data.ar_overdue_total ?? 0),
    ar_due_today_total: Number(data.ar_due_today_total ?? 0),
    ar_due_soon_total: Number(data.ar_due_soon_total ?? 0),
    overdue_ar_by_customer: Array.isArray(data.overdue_ar_by_customer) ? data.overdue_ar_by_customer : [],
    due_soon_days: Number(data.due_soon_days ?? 7),
    pending_review_sales_orders: Array.isArray(data.pending_review_sales_orders) ? data.pending_review_sales_orders : [],
    orders_in_progress: Array.isArray(data.orders_in_progress) ? data.orders_in_progress : [],
    sales_orders_pending_work_order: Array.isArray(data.sales_orders_pending_work_order)
      ? data.sales_orders_pending_work_order
      : [],
    deliveries_not_invoiced: Array.isArray(data.deliveries_not_invoiced) ? data.deliveries_not_invoiced : [],
    delivery_queues: dq ?? {
      delivered_not_invoiced: data.deliveries_not_invoiced ?? [],
      partially_invoiced: [],
      in_transit: [],
      doc_package_pending: [],
    },
    draft_sales_order_count: Number(data.draft_sales_order_count ?? 0),
    draft_sales_invoice_count: Number(data.draft_sales_invoice_count ?? 0),
  };
}

export async function getSalesDashboardCrmFollowUps(): Promise<CrmFollowUpsSummary> {
  const res = await api.get('/sales/dashboard/crm-followups');
  const data = unwrap<CrmFollowUpsSummary>(res);
  return (
    data ?? {
      stale_opportunities: [],
      overdue_activities: [],
      quotations_expiring: [],
      missing_customer_po: [],
    }
  );
}

export async function getSalesDashboardPendingApprovals(): Promise<PendingApprovalRow[]> {
  const res = await api.get('/sales/dashboard/pending-approvals');
  const data = unwrap<PendingApprovalRow[]>(res);
  return Array.isArray(data) ? data : [];
}

export async function getSalesDashboardOrdersAtRisk(): Promise<OrderAtRiskRow[]> {
  const res = await api.get('/sales/dashboard/orders-at-risk');
  const data = unwrap<OrderAtRiskRow[]>(res);
  return Array.isArray(data) ? data : [];
}

export async function getSalesDashboardKpiTrends(params?: {
  as_on_date?: string;
}): Promise<SalesDashboardKpiTrends> {
  const res = await api.get('/sales/dashboard/kpi-trends', { params });
  return (
    unwrap<SalesDashboardKpiTrends>(res) ?? {
      ar_overdue_balance_14d: [],
      invoices_posted_count_14d: [],
      so_confirmed_count_7d: [],
      avg_delivery_to_invoice_lag_days: null,
    }
  );
}

export async function getSalesDashboardOrderHealthSignals(
  ids: string[]
): Promise<OrderHealthSignal[]> {
  if (!ids.length) return [];
  const res = await api.get('/sales/dashboard/order-health-signals', {
    params: { ids: ids.join(',') },
  });
  const data = unwrap<OrderHealthSignal[]>(res);
  return Array.isArray(data) ? data : [];
}

export type QuotationCommercialStage =
  | 'awaiting_customer_order'
  | 'won_pr_awaiting_so'
  | 'converted_to_so'
  | 'other';

export type QuotationAgingBucket = '0-7' | '8-14' | '15-30' | '30+';

export interface QuotationConversionPrSummary {
  id: string;
  request_number: string;
  status: string;
  created_at: string | null;
  link_path: string;
}

export interface QuotationConversionQueueRow {
  id: string;
  quotation_number: string;
  customer_name: string;
  owner_name: string | null;
  sales_team_id: string | null;
  sales_team_name: string | null;
  acceptance_date: string | null;
  days_since_accepted: number;
  aging_bucket: QuotationAgingBucket;
  grand_total: number;
  currency: string;
  valid_until: string | null;
  is_expired: boolean;
  commercial_stage: QuotationCommercialStage;
  commercial_stage_label: string;
  production_requests: QuotationConversionPrSummary[];
  opportunity_id: string | null;
  opportunity_number: string | null;
  opportunity_stage: string | null;
  contract_review_id: string | null;
  contract_review_status: string | null;
  has_contract_review: boolean;
  link_path: string;
  convert_to_so_path: string;
}

export interface QuotationConversionQueuesResult {
  kpis: {
    awaiting_customer_order_count: number;
    awaiting_customer_order_value: number;
    won_pr_awaiting_so_count: number;
    won_pr_awaiting_so_value: number;
    open_commercial_risk_count: number;
    open_commercial_risk_value: number;
    avg_days_since_accepted: number | null;
    max_days_since_accepted: number;
  };
  awaiting_customer_order: QuotationConversionQueueRow[];
  won_pr_awaiting_so: QuotationConversionQueueRow[];
}

export async function getSalesDashboardQuotationConversionQueues(params?: {
  search?: string;
  sales_team_id?: string;
  owner_user_id?: string;
  aging_bucket?: QuotationAgingBucket;
  stage?: 'awaiting_customer_order' | 'won_pr_awaiting_so' | 'all';
  limit?: number;
}): Promise<QuotationConversionQueuesResult> {
  const res = await api.get('/sales/dashboard/quotation-conversion-queues', { params });
  const data = unwrap<QuotationConversionQueuesResult>(res);
  return (
    data ?? {
      kpis: {
        awaiting_customer_order_count: 0,
        awaiting_customer_order_value: 0,
        won_pr_awaiting_so_count: 0,
        won_pr_awaiting_so_value: 0,
        open_commercial_risk_count: 0,
        open_commercial_risk_value: 0,
        avg_days_since_accepted: null,
        max_days_since_accepted: 0,
      },
      awaiting_customer_order: [],
      won_pr_awaiting_so: [],
    }
  );
}

export type AttentionSeverity = 'critical' | 'attention' | 'info';

export interface SalesAttentionBriefItem {
  id: string;
  severity: AttentionSeverity;
  title: string;
  reason: string;
  count?: number;
  amount?: number;
  owner_hint?: string | null;
  section?: string | null;
  link_path: string;
  action_label: string;
}

export interface SalesAttentionBriefResult {
  generated_at: string;
  highlights: string[];
  items: SalesAttentionBriefItem[];
}

export interface SalesOutlookResult {
  generated_at: string;
  weighted_pipeline_value: number;
  open_opportunity_count: number;
  open_accepted_quote_value: number;
  open_accepted_quote_count: number;
  trailing_win_rate_pct: number | null;
  estimated_bookings: number | null;
  estimated_bookings_label: string;
  ar_overdue_total: number;
  avg_delivery_to_invoice_lag_days: number | null;
  ar_overdue_trend: KpiTrendPoint[];
  invoices_posted_trend: KpiTrendPoint[];
  so_confirmed_trend: KpiTrendPoint[];
  analytics_overview_path: string;
  owner_intelligence_path: string;
}

export async function getSalesDashboardAttentionBrief(params?: {
  as_on_date?: string;
  due_soon_days?: number;
}): Promise<SalesAttentionBriefResult> {
  const res = await api.get('/sales/dashboard/attention-brief', { params });
  const data = unwrap<SalesAttentionBriefResult>(res);
  return (
    data ?? {
      generated_at: new Date().toISOString(),
      highlights: [],
      items: [],
    }
  );
}

export async function getSalesDashboardOutlook(params?: {
  as_on_date?: string;
}): Promise<SalesOutlookResult> {
  const res = await api.get('/sales/dashboard/outlook', { params });
  const data = unwrap<SalesOutlookResult>(res);
  return (
    data ?? {
      generated_at: new Date().toISOString(),
      weighted_pipeline_value: 0,
      open_opportunity_count: 0,
      open_accepted_quote_value: 0,
      open_accepted_quote_count: 0,
      trailing_win_rate_pct: null,
      estimated_bookings: null,
      estimated_bookings_label: 'Estimate from trailing win rate × open accepted quotes',
      ar_overdue_total: 0,
      avg_delivery_to_invoice_lag_days: null,
      ar_overdue_trend: [],
      invoices_posted_trend: [],
      so_confirmed_trend: [],
      analytics_overview_path: '/growth/dashboard?tab=analytics&analytics_tab=overview',
      owner_intelligence_path: '/owner-intelligence/sales-pipeline-intelligence',
    }
  );
}
